import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { MapContainer, Marker, Polyline, Popup, TileLayer, useMap } from "react-leaflet";
import {
  FiActivity,
  FiArrowUpRight,
  FiClock,
  FiLayers,
  FiMapPin,
  FiNavigation,
  FiRefreshCcw,
  FiSearch,
  FiUser,
  FiUsers,
} from "react-icons/fi";
import { HiOutlineBuildingOffice2 } from "react-icons/hi2";
import { LuRoute, LuSignal } from "react-icons/lu";
import L from "leaflet";
import API from "../services/api";
import "./AdminLiveTrackingPage.css";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const FitMapBounds = ({ points = [] }) => {
  const map = useMap();
  useEffect(() => {
    if (!Array.isArray(points) || points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 16);
      return;
    }
    map.fitBounds(points, { padding: [36, 36] });
  }, [map, points]);
  return null;
};

const fmtDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const fmtMinutesAgo = (mins) => {
  if (mins == null) return "—";
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem ? `${hours}h ${rem}m ago` : `${hours}h ago`;
};

const fmtDistance = (valueMeters) => {
  const meters = Number(valueMeters);
  if (!Number.isFinite(meters) || meters <= 0) return "0 m";
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(2)} km`;
};

const labelForEvent = (type) => {
  if (type === "punch_in") return "Punch In";
  if (type === "punch_out") return "Punch Out";
  if (type === "meeting_start") return "Meeting Start";
  if (type === "meeting_end") return "Meeting End";
  return "Location Update";
};

const toneForEvent = (type) => {
  if (type === "punch_in") return "start";
  if (type === "punch_out") return "end";
  if (type === "meeting_start" || type === "meeting_end") return "meeting";
  return "update";
};

const coordsOf = (entry) => {
  const lat = Number(entry?.location?.lat ?? entry?.latestLocation?.lat);
  const lng = Number(entry?.location?.lng ?? entry?.latestLocation?.lng);
  return Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : null;
};

const formatCoords = (entry) => {
  const lat = Number(entry?.location?.lat ?? entry?.latestLocation?.lat);
  const lng = Number(entry?.location?.lng ?? entry?.latestLocation?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return "—";
  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
};

const getInitials = (name = "") =>
  String(name)
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "EM";

const isStopPoint = (point, index, points = []) => {
  if (point?.type !== "location_update") return false;
  const segment = Number(point?.segmentDistanceMeters || 0);
  const nextSegment = Number(points[index + 1]?.segmentDistanceMeters || 0);
  return segment <= 15 || nextSegment <= 15;
};

const getMarkerIcon = (index, point, points = []) => {
  const tone = toneForEvent(point?.type);
  const stopped = isStopPoint(point, index, points);
  const glyph =
    point?.type === "punch_in"
      ? "IN"
      : point?.type === "punch_out"
        ? "OUT"
        : point?.type === "meeting_start"
          ? "MT"
          : point?.type === "meeting_end"
            ? "ME"
          : stopped
              ? "STOP"
              : "GO";
  return L.divIcon({
    className: "altp-marker-shell",
    html: `
      <div class="altp-point-cluster ${stopped ? "is-stop" : ""}">
        <div class="altp-point-marker is-${tone} ${stopped ? "is-stop" : ""}">
          <span>${glyph}</span>
        </div>
        ${stopped ? '<div class="altp-point-tag">Stopped here</div>' : ""}
      </div>
    `,
    iconSize: [88, stopped ? 52 : 34],
    iconAnchor: [44, 24],
    popupAnchor: [0, -18],
  });
};

const InfoRow = ({ icon, label, value }) => (
  <div className="altp-info-row">
    <span className="altp-info-icon">{icon}</span>
    <span className="altp-info-label">{label}</span>
    <span className="altp-info-value">{value || "—"}</span>
  </div>
);

const AdminLiveTrackingPage = () => {
  const [rows, setRows] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const fetchTracking = async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const res = await API.get("/admin/attendance-live-tracking");
      const nextRows = Array.isArray(res.data?.employees) ? res.data.employees : [];
      setRows(nextRows);
      setError("");
      setSelectedEmployeeId((prev) => {
        if (!prev) return nextRows[0]?.employeeId || "";
        return nextRows.some((item) => item.employeeId === prev) ? prev : nextRows[0]?.employeeId || "";
      });
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load live tracking");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchTracking();
    const id = window.setInterval(() => fetchTracking(true), 10000);
    return () => window.clearInterval(id);
  }, []);

  const selectedEmployee = useMemo(
    () => rows.find((item) => item.employeeId === selectedEmployeeId) || rows[0] || null,
    [rows, selectedEmployeeId]
  );

  const routePoints = selectedEmployee?.routePoints || [];

  const mapPoints = useMemo(() => {
    if (selectedEmployee) return routePoints.map((point) => coordsOf(point)).filter(Boolean);
    return rows.map((item) => coordsOf(item)).filter(Boolean);
  }, [rows, selectedEmployee, routePoints]);

  const metrics = useMemo(() => {
    const totalTrackedPoints = selectedEmployee
      ? routePoints.length
      : rows.reduce((sum, item) => sum + (item.routePoints?.length || 0), 0);

    const totalDistanceMeters = selectedEmployee
      ? Number(selectedEmployee.routeSummary?.totalDistanceMeters || 0)
      : rows.reduce((sum, item) => sum + Number(item.routeSummary?.totalDistanceMeters || 0), 0);

    return [
      {
        label: "Field Employees Today",
        value: rows.length,
        subtitle: "Active in field",
        icon: <FiUsers />,
        tone: "violet",
      },
      {
        label: "Live Right Now",
        value: rows.filter((item) => item.isLive).length,
        subtitle: "Employees live",
        icon: <FiActivity />,
        tone: "green",
      },
      {
        label: "Meeting Running",
        value: rows.filter((item) => item.meetingMode).length,
        subtitle: "Active meetings",
        icon: <LuSignal />,
        tone: "amber",
      },
      {
        label: "Tracked Points",
        value: totalTrackedPoints,
        subtitle: "Total points today",
        icon: <LuRoute />,
        tone: "blue",
      },
      {
        label: "Distance Since Punch-In",
        value: fmtDistance(totalDistanceMeters),
        subtitle: "Total distance",
        icon: <FiNavigation />,
        tone: "pink",
      },
    ];
  }, [rows, selectedEmployee, routePoints]);

  const stopCards = useMemo(
    () =>
      routePoints.map((point, index) => ({
        ...point,
        index,
        eventLabel: labelForEvent(point.type),
        tone: toneForEvent(point.type),
        coordsText: formatCoords(point),
        segmentText: fmtDistance(point.segmentDistanceMeters),
        totalText: fmtDistance(point.totalDistanceMeters),
      })),
    [routePoints]
  );

  return (
    <div className="altp-page">
      <div className="altp-hero">
        <div className="altp-title-block">
          <div className="altp-title-row">
            <h1>Employee Live Tracking</h1>
            <span className={`altp-live-pill ${selectedEmployee?.isLive ? "is-live" : ""}`}>
              <span className="altp-live-dot" />
              {selectedEmployee?.isLive ? "Live" : "Idle"}
            </span>
          </div>
          <p>Field employees ka latest location, meeting status, aur aaj ka route trail.</p>
        </div>

        <div className="altp-toolbar">
          <div className="altp-select-box">
            <span className="altp-select-icon">
              <FiUser />
            </span>
            <Form.Select
              value={selectedEmployeeId}
              onChange={(e) => setSelectedEmployeeId(e.target.value)}
              className="altp-select"
            >
              {rows.length === 0 ? <option value="">No field employee found</option> : null}
              {rows.map((item) => (
                <option key={item.employeeId} value={item.employeeId}>
                  {item.fullName} {item.isLive ? "(Live)" : ""}
                </option>
              ))}
            </Form.Select>
          </div>

          <Button className="altp-refresh-btn" onClick={() => fetchTracking(true)} disabled={refreshing}>
            <FiRefreshCcw />
            <span>{refreshing ? "Refreshing..." : "Refresh"}</span>
          </Button>
        </div>
      </div>

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {selectedEmployee?.attendanceMode === "field" ? (
        <Alert variant="info" className="mb-4">
          <strong>{selectedEmployee.fullName}</strong>: {selectedEmployee.assignmentMessage || "You are set for field."}
        </Alert>
      ) : null}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          <div className="altp-metric-grid">
            {metrics.map((metric) => (
              <Card key={metric.label} className={`altp-metric-card tone-${metric.tone}`}>
                <Card.Body>
                  <div className="altp-metric-icon">{metric.icon}</div>
                  <div className="altp-metric-copy">
                    <div className="altp-metric-label">{metric.label}</div>
                    <div className="altp-metric-value">{metric.value}</div>
                    <div className="altp-metric-subtitle">{metric.subtitle}</div>
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>

          <Row className="g-4 align-items-start">
            <Col xl={8}>
              <Card className="altp-map-card">
                <Card.Body>
                  <div className="altp-section-head">
                    <div>
                      <div className="altp-section-title">
                        <LuRoute /> {selectedEmployee ? `${selectedEmployee.fullName} Route` : "Field Team Map"}
                      </div>
                      <div className="altp-section-subtitle">
                        Embedded map feel ke saath exact route points aur stop markers.
                      </div>
                    </div>
                    {selectedEmployee?.latestLocation?.lat && selectedEmployee?.latestLocation?.lng ? (
                      <a
                        href={`https://www.google.com/maps?q=${selectedEmployee.latestLocation.lat},${selectedEmployee.latestLocation.lng}`}
                        target="_blank"
                        rel="noreferrer"
                        className="altp-map-link"
                      >
                        Open in Google Maps <FiArrowUpRight />
                      </a>
                    ) : null}
                  </div>

                  {mapPoints.length === 0 ? (
                    <div className="text-muted">Abhi tak koi GPS point capture nahi hua.</div>
                  ) : (
                    <>
                      <div className="altp-map-wrap">
                        <div className="altp-map-overlay altp-map-overlay-top">
                          <div className="altp-map-search">
                            <FiSearch />
                            <span>{selectedEmployee?.latestAddress || "Search location or route point"}</span>
                          </div>
                          <div className="altp-map-actions">
                            <div className="altp-map-status is-google">
                              Google Map API Licence is Activated
                            </div>
                            <button type="button" className="altp-map-chip is-active">
                              <FiMapPin />
                              <span>Route</span>
                            </button>
                            <button type="button" className="altp-map-chip">
                              <FiLayers />
                              <span>Map</span>
                            </button>
                          </div>
                        </div>

                        <MapContainer center={mapPoints[0]} zoom={15} style={{ height: "100%", width: "100%" }}>
                          <TileLayer
                            attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors © <a href="https://carto.com/">CARTO</a>'
                            url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                          />
                          <FitMapBounds points={mapPoints} />
                          {selectedEmployee ? (
                            <>
                              <Polyline positions={mapPoints} pathOptions={{ color: "#93c5fd", weight: 10, opacity: 0.55 }} />
                              <Polyline positions={mapPoints} pathOptions={{ color: "#2563eb", weight: 5, opacity: 0.95 }} />
                              {routePoints.map((point, index) => {
                                const coords = coordsOf(point);
                                if (!coords) return null;
                                const stopped = isStopPoint(point, index, routePoints);
                                return (
                                  <Marker
                                    key={`${point.type}-${point.at}-${index}`}
                                    position={coords}
                                    icon={getMarkerIcon(index, point, routePoints)}
                                  >
                                    <Popup>
                                      <strong>{labelForEvent(point.type)}</strong>
                                      <br />
                                      Marker: {stopped ? "Stopped here" : `Point #${index + 1}`}
                                      <br />
                                      {fmtDateTime(point.at)}
                                      <br />
                                      {point.address || "Address unavailable"}
                                      <br />
                                      GPS: {formatCoords(point)}
                                      <br />
                                      {stopped ? (
                                        <>
                                          Status: Stayed / paused here
                                          <br />
                                        </>
                                      ) : null}
                                      Segment: {fmtDistance(point.segmentDistanceMeters)}
                                      <br />
                                      Total: {fmtDistance(point.totalDistanceMeters)}
                                    </Popup>
                                  </Marker>
                                );
                              })}
                            </>
                          ) : (
                            rows.map((item) => {
                              const coords = coordsOf(item);
                              if (!coords) return null;
                              return (
                                <Marker key={item.employeeId} position={coords}>
                                  <Popup>
                                    <strong>{item.fullName}</strong>
                                    <br />
                                    {item.latestAddress || "Address unavailable"}
                                    <br />
                                    Last seen: {fmtDateTime(item.lastUpdatedAt)}
                                  </Popup>
                                </Marker>
                              );
                            })
                          )}
                        </MapContainer>

                        <div className="altp-map-overlay altp-map-overlay-bottom">
                          <div className="altp-map-pill">
                            <FiNavigation />
                            <span>{fmtDistance(selectedEmployee?.routeSummary?.totalDistanceMeters)}</span>
                          </div>
                          <div className="altp-map-pill">
                            <FiClock />
                            <span>{fmtMinutesAgo(selectedEmployee?.lastSeenMinutesAgo)}</span>
                          </div>
                          <div className="altp-map-pill">
                            <LuRoute />
                            <span>{routePoints.length} stops</span>
                          </div>
                        </div>
                      </div>

                      <div className="altp-legend">
                        <span><i className="is-start" /> Punch In</span>
                        <span><i className="is-update" /> Location Update</span>
                        <span><i className="is-meeting" /> Meeting Event</span>
                        <span><i className="is-end" /> Punch Out</span>
                      </div>
                    </>
                  )}
                </Card.Body>
              </Card>

              {selectedEmployee ? (
                <Card className="altp-table-card altp-table-card-inline mt-4">
                  <Card.Body>
                    <div className="altp-section-head">
                      <div>
                        <div className="altp-section-title">
                          <FiActivity /> Movement Timeline
                        </div>
                        <div className="altp-section-subtitle">
                          Timeline me address, coordinates, moved distance, aur note clearly dikhega.
                        </div>
                      </div>
                    </div>

                    <Table responsive hover className="altp-timeline-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Event</th>
                          <th>Time</th>
                          <th>Address</th>
                          <th>Coordinates</th>
                          <th>Segment</th>
                          <th>Total</th>
                          <th>Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stopCards.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="text-center text-muted">No tracking timeline available</td>
                          </tr>
                        ) : (
                          stopCards.map((point) => (
                            <tr key={`${point.type}-${point.at}-${point.index}`}>
                              <td>{point.index + 1}</td>
                              <td>
                                <span className={`altp-event-pill is-${point.tone}`}>{point.eventLabel}</span>
                              </td>
                              <td>{fmtDateTime(point.at)}</td>
                              <td>{point.address || "—"}</td>
                              <td>{point.coordsText}</td>
                              <td>{point.segmentText}</td>
                              <td>{point.totalText}</td>
                              <td>{point.note || "—"}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              ) : null}
            </Col>

            <Col xl={4}>
              <div className="altp-side-stack">
                <Card className="altp-side-card">
                  <Card.Body>
                    <div className="altp-section-title mb-3">
                      <FiNavigation /> Latest Snapshot
                    </div>
                    {selectedEmployee ? (
                      <>
                        <div className="altp-profile-head">
                          <div className="altp-avatar">{getInitials(selectedEmployee.fullName)}</div>
                          <div className="altp-profile-copy">
                            <div className="altp-profile-name">{selectedEmployee.fullName}</div>
                            <div className="altp-profile-role">{selectedEmployee.roleName || "Employee"}</div>
                            <Badge bg={selectedEmployee.isLive ? "success" : "secondary"}>
                              {selectedEmployee.isLive ? "Live" : "Idle"}
                            </Badge>
                          </div>
                        </div>

                        <div className="altp-info-grid">
                          <InfoRow icon={<FiUser />} label="Mobile" value={selectedEmployee.mobile} />
                          <InfoRow icon={<HiOutlineBuildingOffice2 />} label="Department" value={selectedEmployee.departmentName} />
                          <InfoRow icon={<LuSignal />} label="Status" value={selectedEmployee.status} />
                          <InfoRow icon={<FiActivity />} label="Meeting" value={selectedEmployee.meetingMode ? "Running" : "Not running"} />
                          <InfoRow icon={<FiClock />} label="Last Seen" value={fmtMinutesAgo(selectedEmployee.lastSeenMinutesAgo)} />
                          <InfoRow icon={<FiNavigation />} label="Updated" value={fmtDateTime(selectedEmployee.lastUpdatedAt)} />
                          <InfoRow icon={<FiMapPin />} label="Office Distance" value={fmtDistance(selectedEmployee.officeDistanceMeters)} />
                          <InfoRow icon={<LuRoute />} label="Total Travel" value={fmtDistance(selectedEmployee.routeSummary?.totalDistanceMeters)} />
                          <InfoRow icon={<FiNavigation />} label="Last Segment" value={fmtDistance(selectedEmployee.routeSummary?.lastMovementMeters)} />
                          <InfoRow icon={<FiUsers />} label="Tracked Points" value={selectedEmployee.routeSummary?.trackedPoints || 0} />
                          <InfoRow
                            icon={<FiClock />}
                            label="Punch-In Start"
                            value={fmtDateTime(selectedEmployee.sessionStartedAt || selectedEmployee.punchInAt)}
                          />
                          <InfoRow icon={<FiMapPin />} label="GPS" value={formatCoords(selectedEmployee)} />
                        </div>

                        <div className="altp-address-block">
                          <div className="altp-address-title">Address</div>
                          <div>{selectedEmployee.latestAddress || "Address unavailable"}</div>
                        </div>
                      </>
                    ) : (
                      <div className="text-muted">Field employee select kijiye.</div>
                    )}
                  </Card.Body>
                </Card>

                <Card className="altp-side-card">
                  <Card.Body>
                    <div className="altp-section-title mb-3">
                      <FiUsers /> Team Snapshot
                    </div>
                    <div className="altp-team-list">
                      {rows.map((item) => (
                        <button
                          key={item.employeeId}
                          type="button"
                          className={`altp-team-card ${item.employeeId === selectedEmployee?.employeeId ? "is-active" : ""}`}
                          onClick={() => setSelectedEmployeeId(item.employeeId)}
                        >
                          <div className="altp-team-avatar">{getInitials(item.fullName)}</div>
                          <div className="altp-team-copy">
                            <div className="altp-team-head">
                              <strong>{item.fullName}</strong>
                              <Badge bg={item.isLive ? "success" : "secondary"}>
                                {item.isLive ? "Live" : "Idle"}
                              </Badge>
                            </div>
                            <div className="altp-team-address">{item.latestAddress || "Address unavailable"}</div>
                            <div className="altp-team-meta">
                              {item.routePoints?.length || 0} points | {fmtDistance(item.routeSummary?.totalDistanceMeters)}
                            </div>
                            <div className="altp-team-meta">{fmtMinutesAgo(item.lastSeenMinutesAgo)}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              </div>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default AdminLiveTrackingPage;
